/**
 * הגדרות — the firm, its plan usage, the onboarding checklist and the audit trail.
 *
 * The hourly cost is the setting that matters most and is easiest to miss: it is the
 * denominator of every profitability figure, so a firm that never sets it reads its whole
 * book as pure profit. That is why it has its own panel with an explanation rather than
 * sitting in a generic key-value list.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime } from "@onecpa/shared";
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Fact,
  Field,
  Input,
  KpiStrip,
  PageHeader,
  Panel,
  Segmented,
  TabStrip,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { useRole, useSession } from "../app/session.js";
import { THEME_CYCLE, THEME_META, useTheme, type ThemeChoice } from "../app/theme.js";

const ONBOARDING_LABELS: Record<string, string> = {
  firm_created: "הוקם המשרד",
  clients_imported: "נטענו לקוחות",
  frequencies_set: "הוגדרו תדירויות דיווח",
  portal_enabled: "הופעל פורטל הלקוחות",
};

const PLAN_LABELS: Record<string, string> = {
  solo: "Solo",
  office: "Office",
  enterprise: "Enterprise",
};

type TabId = "firm" | "audit";

export function Settings() {
  const session = useSession();
  const { isOwner, isManager } = useRole();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabId>("firm");
  const { choice: themeChoice, setTheme } = useTheme();

  const organization = useQuery({
    queryKey: ["organization", session.orgId],
    queryFn: () => api.settings.organization(session.orgId),
  });

  const audit = useQuery({
    queryKey: ["audit", session.orgId],
    queryFn: () => api.analytics.audit(session.orgId),
    enabled: isManager && tab === "audit",
  });

  const updateOrg = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.settings.updateOrganization(session.orgId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organization"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      session.toast("ההגדרות נשמרו");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const putSetting = useMutation({
    mutationFn: (input: { key: string; value: Record<string, unknown> }) =>
      api.settings.putSetting(session.orgId, input.key, input.value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organization"] });
      session.toast("ההגדרה נשמרה");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  if (organization.error) {
    return (
      <ErrorState
        message={(organization.error as Error).message}
        onRetry={() => void organization.refetch()}
      />
    );
  }

  const org = organization.data?.org;
  const usage = organization.data?.usage;
  const settings = organization.data?.settings ?? [];
  const hourlyCost = readNumber(settings.find((row) => row.key === "hourly_cost")?.value, "agorot");
  const billing = settings.find((row) => row.key === "billing")?.value;

  return (
    <>
      <PageHeader title="הגדרות" subtitle={session.orgName}>
        <TabStrip
          tabs={[
            { id: "firm", label: "המשרד" },
            ...(isManager ? [{ id: "audit", label: "יומן ביקורת" }] : []),
          ]}
          active={tab}
          onSelect={(id) => setTab(id as TabId)}
        />
      </PageHeader>

      <KpiStrip
        loading={organization.isLoading}
        items={[
          {
            label: "תוכנית",
            value: org ? (PLAN_LABELS[org.plan] ?? org.plan) : undefined,
            tone: "ledger",
          },
          {
            label: "לקוחות",
            value: org && usage ? `${usage.clients}/${org.maxClients}` : undefined,
            tone: (usage?.clients ?? 0) >= (org?.maxClients ?? Infinity) ? "ochre" : "ink",
          },
          {
            label: "מושבים",
            value: !org || !usage ? undefined : org.maxSeats ? `${usage.seats}/${org.maxSeats}` : "ללא הגבלה",
          },
          {
            label: "מצב",
            value:
              org?.status === "trialing"
                ? "תקופת ניסיון"
                : org?.status === "active"
                  ? "פעיל"
                  : (org?.status ?? "—"),
            tone: org?.status === "trialing" ? "pending" : "ledger",
          },
          {
            label: "פורטל לקוחות",
            value: org?.portalEnabled ? "פעיל" : "כבוי",
            tone: org?.portalEnabled ? "ledger" : "ink",
          },
        ]}
      />

      <div className="grid gap-4 p-5 lg:grid-cols-2">
        {tab === "firm" && (
          <>
            <Panel title="פרטי המשרד">
              <div className="flex flex-col gap-3 px-3.5 py-3">
                {/* Rendered only once the query has resolved, and keyed on the value.
                    A form whose state is seeded from async data initialises to the
                    placeholder and never catches up — which showed as an empty firm
                    name field over a firm that plainly has one. */}
                {org && (
                  <FirmNameForm
                    key={org.name}
                    initial={org.name}
                    disabled={!isOwner}
                    onSave={(name) => updateOrg.mutate({ name })}
                    saving={updateOrg.isPending}
                  />
                )}
                <Checkbox
                  label="פורטל לקוחות פעיל"
                  checked={org?.portalEnabled ?? false}
                  disabled={!isOwner}
                  onChange={(event) => updateOrg.mutate({ portalEnabled: event.target.checked })}
                />
                <p className="text-2xs text-ink-faint">
                  הפורטל כבוי כברירת מחדל. כשהוא פעיל, לקוחות שהוזמנו יכולים לאשר העברת חומר ולראות
                  את המסמכים שסומנו כגלויים להם.
                </p>
              </div>
            </Panel>

            <Panel title="עלות שעת עבודה">
              <div className="flex flex-col gap-3 px-3.5 py-3">
                {hourlyCost !== null && (
                  <HourlyCostForm
                    key={hourlyCost}
                    initialAgorot={hourlyCost}
                    disabled={!isOwner}
                    saving={putSetting.isPending}
                    onSave={(agorotValue) =>
                      putSetting.mutate({ key: "hourly_cost", value: { agorot: agorotValue } })
                    }
                  />
                )}
                <p className="text-2xs text-ink-faint">
                  זהו המכנה של כל חישוב רווחיות: ריטיינר פחות שעות שנרשמו כפול עלות השעה. משרד שלא
                  מגדיר אותו יראה את כל הלקוחות כרווחיים.
                </p>
              </div>
            </Panel>

            <Panel title="גבייה">
              <dl className="grid grid-cols-2 gap-3 px-3.5 py-3">
                <Fact label="שיעור מע״מ">{readNumber(billing, "vatRate") ?? 18}%</Fact>
                <Fact label="תנאי תשלום">{readNumber(billing, "paymentTermsDays") ?? 30} יום</Fact>
              </dl>
              <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
                המערכת עוקבת אחר מה שהמשרד גובה. היא אינה תוכנת הנהלת חשבונות ואינה מפיקה חשבוניות
                מס.
              </p>
            </Panel>

            <Panel title="ערכת נושא">
              <div className="px-3.5 py-3">
                <Segmented
                  options={THEME_CYCLE.map((value) => ({
                    value,
                    label: THEME_META[value].label,
                  }))}
                  value={themeChoice}
                  onChange={(value: ThemeChoice) => setTheme(value)}
                />
                <p className="mt-2 text-2xs text-ink-faint">
                  ״לפי המערכת״ עוקב אחר הגדרת המכשיר ומתחלף איתה.
                </p>
              </div>
            </Panel>

            <Panel title="השלמת הקמה">
              <ul className="divide-y divide-line-soft">
                {(organization.data?.onboarding ?? []).map((step) => (
                  <li key={step.step} className="flex items-center gap-3 px-3.5 py-2">
                    <span className="flex-1 text-xs text-ink">
                      {ONBOARDING_LABELS[step.step] ?? step.step}
                    </span>
                    {step.completedAt ? (
                      <Badge tone="success">הושלם</Badge>
                    ) : (
                      <Badge tone="pending">ממתין</Badge>
                    )}
                  </li>
                ))}
              </ul>
              {org?.trialEndsAt && org.status === "trialing" && (
                <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ochre">
                  תקופת הניסיון מסתיימת ב-<bdi className="tnum">{formatDate(org.trialEndsAt)}</bdi>
                </p>
              )}
            </Panel>
          </>
        )}

        {tab === "audit" && isManager && (
          <Panel title="יומן ביקורת" className="lg:col-span-2">
            {(audit.data?.events ?? []).length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {(audit.data?.events ?? []).map((event) => (
                  <li key={event.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2">
                    <bdi className="tnum w-36 text-2xs text-ink-faint">
                      {formatDateTime(event.createdAt)}
                    </bdi>
                    <span className="text-xs font-semibold text-ink" dir="ltr">
                      {event.action}
                    </span>
                    <bdi
                      className="min-w-32 flex-1 truncate text-2xs text-ink-muted"
                      dir="ltr"
                    >
                      {event.actorEmail ?? "system"}
                    </bdi>
                    {event.ip && (
                      <bdi className="tnum hidden text-2xs text-ink-faint md:block" dir="ltr">
                        {event.ip}
                      </bdi>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="היומן ריק" />
            )}
            <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
              היומן הוא לקריאה בלבד: לתפקיד שהאפליקציה מתחברת בו אין הרשאת עדכון או מחיקה על הטבלה
              הזו, כנדרש בתיקון 13.
            </p>
          </Panel>
        )}
      </div>
    </>
  );
}

function FirmNameForm({
  initial,
  disabled,
  saving,
  onSave,
}: {
  initial: string;
  disabled: boolean;
  saving: boolean;
  onSave(name: string): void;
}) {
  const [name, setName] = useState(initial);

  return (
    <div className="flex items-end gap-2">
      <Field label="שם המשרד" className="flex-1">
        {({ id }) => (
          <Input
            id={id}
            value={name}
            disabled={disabled}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
      <Button
        variant="primary"
        disabled={disabled || name.trim() === initial || name.trim().length === 0}
        loading={saving}
        onClick={() => onSave(name.trim())}
      >
        שמירה
      </Button>
    </div>
  );
}

function HourlyCostForm({
  initialAgorot,
  disabled,
  saving,
  onSave,
}: {
  initialAgorot: number;
  disabled: boolean;
  saving: boolean;
  onSave(agorot: number): void;
}) {
  const [shekels, setShekels] = useState((initialAgorot / 100).toFixed(2));

  return (
    <div className="flex items-end gap-2">
      <Field label="עלות שעה (₪)" className="flex-1">
        {({ id }) => (
          <Input
            id={id}
            type="number"
            min={0}
            step="0.01"
            disabled={disabled}
            value={shekels}
            onChange={(event) => setShekels(event.target.value)}
          />
        )}
      </Field>
      <Button
        variant="primary"
        disabled={disabled || Number(shekels) <= 0}
        loading={saving}
        // Agorot at the boundary: the value is stored as an integer so no rounding can
        // creep into a margin.
        onClick={() => onSave(Math.round(Number(shekels) * 100))}
      >
        שמירה
      </Button>
    </div>
  );
}

function readNumber(value: unknown, key: string): number | null {
  if (value && typeof value === "object" && key in value) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate === "number") return candidate;
    if (
      typeof candidate === "string" &&
      candidate.trim() !== "" &&
      !Number.isNaN(Number(candidate))
    ) {
      return Number(candidate);
    }
  }
  return null;
}
